// Vendors
import { sessionUtils } from '@openbank/cf-ui-architecture-core';
// Translations
import {
  CONNECTION_ALREADY_CLOSED,
  CONNECTION_ALREADY_OPENED,
  CONNECTION_NOT_OPENED,
} from './translations/websocket.translations';
// Types
import { IAsyncActiveConnection } from './types/websocket-connection.types';
import { IEventsCallback } from './types/websocket-events.types';
import { WebSocketPropsType } from './types/websocket.types';

class WebSocketsClass implements IAsyncActiveConnection<string> {
  private readonly url: WebSocketPropsType['url'];
  private readonly protocols: WebSocketPropsType['protocols'];
  private websocket?: WebSocket;
  private eventsCallback: IEventsCallback<string>;
  private isConnectionOpen = false;
  constructor({ url, protocols }: WebSocketPropsType) {
    this.eventsCallback = {
      onReceiveData: (_data: string): null => null,
      onError: (_error: Error): null => null,
    };
    this.url = url;
    this.protocols = protocols;
  }
  private notifyConnect(): void {
    this.eventsCallback?.onConnect?.();
  }
  private notifyDisconnect(): void {
    this.eventsCallback?.onDisconnect?.();
  }
  private notifyReceiveData(data: string): void {
    this.eventsCallback.onReceiveData(data);
  }
  private notifyError(error: Error): void {
    this.eventsCallback.onError(error);
  }
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isConnectionOpen) {
        try {
          this.websocket = new WebSocket(this.url, this.protocols);
          this.websocket.onopen = async () => {
            if (this.websocket?.readyState === WebSocket.OPEN) {
              this.isConnectionOpen = true;
              await sessionUtils.refreshToken({ isGil: true });
              this.notifyConnect();
              resolve();
            }
          };
          this.websocket.onmessage = event => {
            this.notifyReceiveData(event.data);
          };
          this.websocket.onerror = error => {
            this.notifyError(new Error(error.toString()));
          };
        } catch (error) {
          reject(error);
        }
      } else {
        reject(new Error(CONNECTION_ALREADY_OPENED));
      }
    });
  }
  disconnect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnectionOpen) {
        try {
          this.websocket?.close();
          this.isConnectionOpen = false;
          this.notifyDisconnect();
          resolve();
        } catch (error) {
          reject(error);
        }
      } else {
        reject(new Error(CONNECTION_ALREADY_CLOSED));
      }
    });
  }
  send(data: string): void {
    if (this.isConnectionOpen) {
      this.websocket?.send(data);
    } else {
      this.notifyError(new Error(CONNECTION_NOT_OPENED));
    }
  }
  listen(eventsCallback: IEventsCallback<string>): void {
    this.eventsCallback = { ...this.eventsCallback, ...eventsCallback };
  }
}

export { WebSocketsClass };
